import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { eventTypeFormSchema, type EventTypeFormValues } from './eventTypeFormSchema'
import type { EventType, EventTypeUpsert } from '../types'

interface Props {
  visible: boolean
  initial: EventType | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: EventTypeUpsert) => void
  onHide: () => void
}

/** Etkinlik türü ekle/düzenle formu (023: react-hook-form + zod). TR ad zorunlu; EN opsiyonel. Sıra formda yok. */
export default function EventTypeFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(eventTypeFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: EventTypeFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={
        initial ? t('activity.eventTypes.form.editTitle') : t('activity.eventTypes.form.newTitle')
      }
      submitLabel={t('activity.eventTypes.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('activity.eventTypes.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('activity.eventTypes.form.nameEn')}
        />
      </div>
    </FormDialog>
  )
}
